<script setup>
import { useTime } from 'src/composables/time';
import {
  HrDivider, HrIcon, HrListAddBtn, HrListExpansion, HrListItem, HrListSectionHeader, HrLoadingSpinner,
  HrOnOffSwitch,
} from 'src/design-system';

/**
 * @typedef {Object} ChartLineGroupScope
 * @property {string} value
 * @property {string} label
 * @property {string} caption
 * @property {string} scope
 * @property {string | null} [program]
 * @property {string | null} [target]
 * @property {boolean} isSelected Whether this group's lines of the scope are switched on
 * @property {boolean} isLoaded
 * @property {Object[]} lines This group's lines in the scope
 */

defineProps({
  title: {
    type: String,
    required: true,
  },
  addLabel: {
    type: String,
    required: true,
  },
  switchLabel: {
    type: Function,
    required: true,
  },
  localLines: {
    type: Array,
    default: () => [],
  },
  /** @type {import('vue').PropType<ChartLineGroupScope[]>} */
  scopes: {
    type: Array,
    default: () => [],
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  edit: null,
  add: null,
  toggleScope: null,
  loadScope: null,
});

const { getLocaleDateString } = useTime();

const formatDate = (timestamp) => (
  typeof timestamp?.toDate === 'function' ? getLocaleDateString(timestamp) : ''
);

const onScopeOpened = (scope, isOpen) => {
  if (isOpen) {
    emit('loadScope', scope);
  }
};
</script>

<template>
  <hr-divider />
  <hr-list-section-header>
    {{ title }}
  </hr-list-section-header>
  <hr-list-item
    v-for="line in localLines"
    :key="line.id"
    :label="line.label"
    :caption="formatDate(line.startTimestamp)"
    :disable="!canEdit"
    clickable
    @click="emit('edit', line)"
  >
    <template
      v-if="canEdit"
      #trailing
    >
      <hr-icon name="edit" />
    </template>
  </hr-list-item>
  <hr-divider />
  <hr-list-expansion
    v-for="scope in scopes"
    :key="scope.value"
    :label="scope.label"
    :caption="scope.caption"
    @update:model-value="onScopeOpened(scope, $event)"
  >
    <template #trailing>
      <hr-on-off-switch
        :model-value="scope.isSelected"
        :aria-label="switchLabel(scope.label)"
        @update:model-value="emit('toggleScope', scope, $event)"
      />
    </template>
    <template v-if="scope.isLoaded">
      <hr-list-item
        v-for="line in scope.lines"
        :key="line.id"
        :label="line.label"
        :caption="formatDate(line.startTimestamp)"
        :disable="!canEdit"
        clickable
        @click="emit('edit', line)"
      >
        <template
          v-if="canEdit"
          #trailing
        >
          <hr-icon name="edit" />
        </template>
      </hr-list-item>
    </template>
    <hr-list-item
      v-else
      :label="$t('common.loading')"
    >
      <template #leading>
        <hr-loading-spinner size="24px" />
      </template>
    </hr-list-item>
    <hr-divider />
  </hr-list-expansion>
  <template v-if="canEdit">
    <hr-divider />
    <hr-list-add-btn
      :label="addLabel"
      @click="emit('add')"
    />
  </template>
</template>
